import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Lock, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useFetch } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { money, imgUrl } from "../lib/shop";
import { DeliveryFields } from "../components/site/DeliveryFields";
import { useQuote, Totals, DiscountBox } from "../components/site/Quote";
import { Seo } from "../components/site/Seo";

const FIELDS = [
  ["first_name", "First name", "text", "given-name"], ["last_name", "Last name", "text", "family-name"],
  ["email", "Email", "email", "email"], ["phone", "Mobile number", "tel", "tel"],
];

export default function Checkout() {
  const { cart, user, zoneId, setZoneId, discountCode } = useStore();
  const nav = useNavigate();
  const zones = useFetch("/delivery-zones");
  const provider = useFetch("/payments/provider");
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", street: "", suburb: "", city: "", province: "", postal_code: "", pep_store: "", mall_name: "", paxi_code: "", within_area: false, instructions: "" });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const quote = useQuote();

  useEffect(() => {
    if (!user) return;
    const a = user.addresses?.[0] || {};
    setF((x) => ({ ...x, first_name: user.first_name || "", last_name: user.last_name || "", email: user.email, phone: user.phone || "",
      street: a.street || "", suburb: a.suburb || "", city: a.city || "", province: a.province || "", postal_code: a.postal_code || "" }));
  }, [user]);

  if (!cart.length) return <Navigate to="/cart" replace />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const zoneList = zones.data || [];
  const method = zoneList.find((z) => z.id === zoneId)?.method;
  const pickAddress = (a) => setF({ ...f, street: a.street, suburb: a.suburb, city: a.city, province: a.province, postal_code: a.postal_code });

  const submit = async (e) => {
    e.preventDefault();
    if (!zoneId) return toast.error("Please choose a delivery option");
    if (!consent) return toast.error("Please accept the privacy notice");
    setBusy(true);
    try {
      const items = cart.map(({ product_id, size, colour, qty }) => ({ product_id, size, colour: colour || "", qty }));
      const r = await api.post("/checkout", { items, zone_id: zoneId, discount_code: discountCode || null, customer: f, popia_consent: consent });
      if (r.data.redirect_url.startsWith("/")) nav(r.data.redirect_url);
      else window.location.href = r.data.redirect_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <div className="container-x py-10 sm:py-16">
      <Seo title="Checkout" />
      <h1 className="font-display text-6xl sm:text-8xl leading-none">Checkout</h1>
      <div className="mt-4 card-dark p-4 flex gap-3 text-sm text-zinc-300 max-w-3xl" data-testid="checkout-online-notice">
        <Truck className="text-gold shrink-0" size={20} />
        <p>Mwezzy Footwear is an <b className="text-white">online store</b> — there is no walk-in shop. Choose <b className="text-white">FREE Uber delivery</b> (Johannesburg, within 20km) or <b className="text-white">FREE PAXI collection</b> at any PEP store.</p>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-10 mt-8" data-testid="checkout-form">
        <div className="space-y-10">
          <section>
            <h2 className="font-display text-3xl mb-4">1. Your details</h2>
            {!user && <p className="text-sm text-zinc-400 mb-4">Checking out as a guest. <Link to="/account" className="text-gold underline">Log in</Link> to use saved addresses (optional).</p>}
            {user?.addresses?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {user.addresses.map((a) => <button type="button" key={a.id} onClick={() => pickAddress(a)} className="chip" data-testid={`checkout-saved-address-${a.id}`}>{a.label}: {a.street}</button>)}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              {FIELDS.map(([k, l, type, ac, wide]) => (
                <div key={k} className={wide ? "sm:col-span-2" : ""}>
                  <label className="label" htmlFor={k}>{l}</label>
                  <input id={k} type={type} autoComplete={ac} required value={f[k]} onChange={set(k)} className="field" data-testid={`checkout-${k.replace("_", "-")}-input`} />
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="font-display text-3xl mb-4">2. Delivery option</h2>
            <div className="space-y-2" data-testid="checkout-zone-list">
              {zoneList.map((z) => (
                <label key={z.id} className={`flex items-center justify-between gap-4 p-4 rounded-xl border cursor-pointer ${zoneId === z.id ? "border-[#d4af37] bg-[#d4af37]/5" : "border-white/10"}`} data-testid={`checkout-zone-${z.id}`}>
                  <span className="flex items-center gap-3">
                    <input type="radio" name="zone" checked={zoneId === z.id} onChange={() => setZoneId(z.id)} className="accent-[#d4af37] w-5 h-5" />
                    <span><span className="font-bold block">{z.name}</span><span className="text-xs text-zinc-400">{z.description}{z.eta ? ` · ${z.eta}` : ""}</span></span>
                  </span>
                  <span className="font-bold shrink-0">{z.fee ? money(z.fee) : "FREE"}</span>
                </label>
              ))}
            </div>
          </section>
          <section>
            <h2 className="font-display text-3xl mb-4">3. {method === "paxi" ? "PEP collection point" : "Delivery address"}</h2>
            <DeliveryFields method={method} f={f} setF={setF} set={set} />
          </section>
        </div>
        <aside className="card-dark p-6 h-fit lg:sticky lg:top-28 space-y-5">
          <h2 className="font-display text-3xl">4. Review & pay</h2>
          <div className="space-y-3 max-h-64 overflow-y-auto" data-testid="checkout-review-items">
            {(quote?.lines || []).map((l) => (
              <div key={l.product_id + (l.colour || "") + l.size} className="flex gap-3 text-sm">
                <img src={imgUrl(l.image)} alt="" className="w-14 h-14 rounded-lg object-cover" />
                <div className="flex-1 min-w-0"><p className="font-semibold truncate">{l.name}</p><p className="text-zinc-400 text-xs">{[l.colour, l.size].filter(Boolean).join(" · ")} × {l.qty} · {money(l.unit_price)}</p>{l.sku && <p className="text-[11px] text-zinc-500">SKU {l.sku}</p>}</div>
                <span className="font-bold">{money(l.line_total)}</span>
              </div>
            ))}
          </div>
          <DiscountBox />
          {quote?.discount_error && <p className="text-xs text-red-400">{quote.discount_error}</p>}
          <Totals quote={quote} />
          {quote?.errors?.length > 0 && <p className="text-xs text-red-400" data-testid="checkout-errors">{quote.errors.join(" · ")}</p>}
          <label className="flex gap-3 text-xs text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="accent-[#d4af37] w-5 h-5 shrink-0" data-testid="checkout-consent-checkbox" />
            <span>I agree that Mwezzy Footwear may process my personal information to fulfil and deliver this order, as described in the <Link to="/privacy" className="underline">Privacy Policy</Link> (POPIA).</span>
          </label>
          <button disabled={busy || quote?.errors?.length > 0} className="btn-gold w-full" data-testid="checkout-submit-button">
            <Lock size={16} /> {busy ? "Starting payment..." : `Pay ${money(quote?.total)} with iKhokha`}
          </button>
          {provider.data?.provider === "demo" && <p className="text-[11px] text-amber-300 text-center" data-testid="checkout-demo-notice">DEMO PAYMENT MODE — iKhokha credentials not yet added. No real money is charged.</p>}
          <p className="text-[11px] text-zinc-500 text-center">Card details are entered on iKhokha's secure payment page. Mwezzy Footwear never sees or stores your card number.</p>
        </aside>
      </form>
    </div>
  );
}
